import React, { useEffect, useState } from "react";
import { useTable } from "@/lib/store";
import { api, API, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Kicker, Chip, Modal, Field, Area, Pick, Empty, Avatar } from "@/components/kit";
import { KeyRound } from "lucide-react";
import { ROMAN, MEETING_TYPES, fmtDate, relTime } from "@/lib/util";
import { DateField } from "@/components/DateField";

const TABS = ["Members", "Chapters", "Year & Roles", "Settings"];

const MembersTab = () => {
  const { members, refreshMembers } = useTable();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", avatarColor: "#1E3A8A", joinedYear: "" });
  const [creds, setCreds] = useState(null);
  const [credForm, setCredForm] = useState({});
  const pending = members.filter((m) => m.status === "Pending");

  const add = async () => {
    try {
      await api.post("/members", form);
      setOpen(false);
      setForm({ name: "", email: "", company: "", phone: "", avatarColor: "#1E3A8A", joinedYear: "" });
      await refreshMembers();
      toast.success("Seat proposed. Approve it to let them sign in.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const approve = async (m) => {
    try {
      await api.post(`/members/${m.id}/approve`);
      await refreshMembers();
      toast.success(`${m.name} approved — set their user ID and PIN so they can sign in.`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const decline = async (m) => {
    try {
      await api.delete(`/members/${m.id}`);
      await refreshMembers();
      toast.success("Seat withdrawn.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleStatus = async (m) => {
    await api.put(`/members/${m.id}`, { status: m.status === "Active" ? "Alumni" : "Active" });
    await refreshMembers();
    toast.success("Status updated.");
  };

  const openCreds = (m) => {
    setCreds(m);
    setCredForm({ userId: m.userId || "", pin: "" });
  };

  const saveCreds = async () => {
    try {
      const body = {};
      if (credForm.userId && credForm.userId !== creds.userId) body.userId = credForm.userId;
      if (credForm.pin) body.pin = credForm.pin;
      if (!Object.keys(body).length) { toast.error("Nothing to change."); return; }
      await api.post(`/members/${creds.id}/credentials`, body);
      await refreshMembers();
      setCreds(null);
      toast.success("Sign-in details saved.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3">
      <button className="btn-ink" data-testid="admin-add-member" onClick={() => setOpen(true)}>Propose member</button>

      {pending.length > 0 && (
        <Card data-testid="admin-pending">
          <Kicker>Awaiting approval · {pending.length}</Kicker>
          <p className="lede !text-[12.5px] mt-1">
            A proposed member cannot sign in until you approve the seat.
          </p>
          <div className="mt-4 divide-hair">
            {pending.map((m) => (
              <div key={m.id} className="flex items-center gap-3 py-3" data-testid={`admin-pending-${m.id}`}>
                <Avatar name={m.name} color={m.avatarColor} size={32} dim />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-medium" style={{ color: "var(--ink)" }}>{m.name}</div>
                  <div className="text-[12px]" style={{ color: "var(--ink-mute)" }}>{m.email}</div>
                </div>
                <button className="btn-ink !min-h-0 !py-2" data-testid={`admin-approve-${m.id}`} onClick={() => approve(m)}>
                  Approve
                </button>
                <button className="btn-quiet !min-h-0 !py-2" data-testid={`admin-decline-${m.id}`} onClick={() => decline(m)}>
                  Decline
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}
      {members.map((m) => (
        <Card key={m.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`admin-member-${m.id}`}>
          <Avatar name={m.name} color={m.avatarColor} size={36} dim={m.status === "Alumni"} />
          <div className="min-w-0 flex-1">
            <div className="serif text-lg">{m.name}</div>
            <div className="text-xs" style={{ color: "var(--ink-mute)" }}>{m.email}</div>
            <div className="text-[11.5px] mt-1 flex gap-3" style={{ color: "var(--ink-mute)" }}>
              <span data-testid={`admin-userid-${m.id}`}>
                ID: <span className="numeral" style={{ color: "var(--ink)" }}>{m.userId || "not set"}</span>
              </span>
              <span data-testid={`admin-pin-state-${m.id}`} style={{ color: m.pinSetAt ? "var(--green)" : "var(--rust)" }}>
                {m.pinSetAt ? "PIN set" : "No PIN — cannot sign in"}
              </span>
            </div>
          </div>
          <Chip color={m.status === "Pending" ? "var(--amber)" : m.status === "Alumni" ? undefined : "var(--green)"}>
            {m.status}
          </Chip>
          <button className="btn-quiet" data-testid={`admin-credentials-${m.id}`} onClick={() => openCreds(m)}>
            <KeyRound size={13} /> User ID & PIN
          </button>
          {m.status === "Pending" ? (
            <button className="btn-ink !min-h-0 !py-2" data-testid={`admin-approve-row-${m.id}`} onClick={() => approve(m)}>
              Approve
            </button>
          ) : (
            <button className="btn-quiet" data-testid={`admin-toggle-status-${m.id}`} onClick={() => toggleStatus(m)}>
              {m.status === "Active" ? "Mark Alumni" : "Reinstate"}
            </button>
          )}
        </Card>
      ))}
      <Modal open={!!creds} onClose={() => setCreds(null)} title={`Sign-in for ${creds?.name || ""}`} testId="admin-credentials-modal">
        <p className="text-[13px] mb-4" style={{ color: "var(--ink-mute)" }}>
          They sign in with this user ID and PIN. Only they can change their own PIN afterwards —
          you can always reset it here.
        </p>
        <Field label="User ID" value={credForm.userId || ""} data-testid="admin-cred-userid"
          onChange={(e) => setCredForm({ ...credForm, userId: e.target.value.toLowerCase() })} />
        <Field label="New PIN (four digits)" value={credForm.pin || ""} data-testid="admin-cred-pin"
          onChange={(e) => setCredForm({ ...credForm, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
        <button className="btn-ink w-full" data-testid="admin-cred-save" onClick={saveCreds}>Save sign-in details</button>
      </Modal>
      <Modal open={open} onClose={() => setOpen(false)} title="Add member" testId="admin-member-modal">
        <Field label="Name" value={form.name} data-testid="admin-member-name" onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Field label="Email" value={form.email} data-testid="admin-member-email" onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Field label="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
        <Field label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <Field label="Avatar colour" value={form.avatarColor} onChange={(e) => setForm({ ...form, avatarColor: e.target.value })} />
        <button className="btn-ink w-full" data-testid="admin-member-save" onClick={add}>Add</button>
      </Modal>
    </div>
  );
};

const ChaptersTab = () => {
  const { meetings, yearId, refresh, activeMembers } = useTable();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const save = async () => {
    try {
      if (editing === "new") await api.post("/meetings", { ...form, yearId, num: Number(form.num || meetings.length + 1) });
      else await api.put(`/meetings/${editing}`, form);
      setEditing(null);
      await refresh();
      toast.success("Saved.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3">
      <button className="btn-ink" data-testid="admin-add-chapter" onClick={() => { setForm({ num: meetings.length + 1, type: "Regular", status: "Planned" }); setEditing("new"); }}>
        Add chapter
      </button>
      {meetings.map((m) => (
        <Card key={m.id} className="p-4 flex items-center gap-4" data-testid={`admin-chapter-${m.num}`}>
          <span className="roman text-3xl w-10 text-center">{ROMAN[m.num - 1]}</span>
          <div className="min-w-0 flex-1">
            <div className="serif text-lg">{m.title || "Untitled"}</div>
            <div className="text-xs" style={{ color: "var(--ink-mute)" }}>{fmtDate(m.date)} · {m.type} · {m.status}</div>
          </div>
          <button className="btn-quiet" data-testid={`admin-edit-chapter-${m.num}`} onClick={() => { setForm({ ...m }); setEditing(m.id); }}>Edit</button>
        </Card>
      ))}
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Chapter" testId="admin-chapter-modal">
        <Field label="Number" type="number" value={form.num || ""} onChange={(e) => setForm({ ...form, num: Number(e.target.value) })} />
        <Field label="Title" value={form.title || ""} data-testid="admin-chapter-title" onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <DateField label="Date" value={form.date || ""} testId="admin-chapter-date" onChange={(v) => setForm({ ...form, date: v })} />
        <Field label="Time" value={form.time || ""} onChange={(e) => setForm({ ...form, time: e.target.value })} />
        <Field label="Venue" value={form.venue || ""} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
        <Field label="Resource speaker" value={form.resourceSpeaker || ""} onChange={(e) => setForm({ ...form, resourceSpeaker: e.target.value })} />
        <Pick label="Type" options={MEETING_TYPES} value={form.type || "Regular"} onChange={(e) => setForm({ ...form, type: e.target.value })} />
        <Pick label="Status" options={["Planned", "Scheduled", "Done"]} value={form.status || "Planned"} onChange={(e) => setForm({ ...form, status: e.target.value })} />
        <Pick
          label="Deep-dive host"
          options={[{ value: "", label: "—" }, ...activeMembers.map((m) => ({ value: m.id, label: m.name }))]}
          value={form.deepDiveHostId || ""}
          onChange={(e) => setForm({ ...form, deepDiveHostId: e.target.value })}
        />
        <button className="btn-ink w-full" data-testid="admin-chapter-save" onClick={save}>Save</button>
      </Modal>
    </div>
  );
};

const YearTab = () => {
  const { year, years, activeMembers, refreshYears, loadIdentity } = useTable();
  const [form, setForm] = useState(year || {});
  const [wizard, setWizard] = useState(false);
  const [nw, setNw] = useState({ newYearId: "", theme: "", northStar: "", startDate: "", endDate: "", moderatorId: "", coModeratorId: "" });

  useEffect(() => { setForm(year || {}); }, [year]);

  const save = async () => {
    try {
      await api.put(`/years/${year.id}`, {
        theme: form.theme, northStar: form.northStar, startDate: form.startDate,
        endDate: form.endDate, moderatorId: form.moderatorId, coModeratorId: form.coModeratorId,
      });
      await refreshYears();
      await loadIdentity();
      toast.success("Year updated. Admin rights follow the chairs.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pass = async () => {
    try {
      await api.post("/years/rollover", nw);
      setWizard(false);
      await refreshYears();
      await loadIdentity();
      toast.success("The table has been passed.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!year) return <Empty line="No year selected." />;

  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <Kicker>{year.id} · {year.status}</Kicker>
        <Field label="Theme" value={form.theme || ""} data-testid="year-theme" onChange={(e) => setForm({ ...form, theme: e.target.value })} />
        <Area label="North Star" value={form.northStar || ""} data-testid="year-northstar" onChange={(e) => setForm({ ...form, northStar: e.target.value })} />
        <DateField label="Start date" value={form.startDate || ""} testId="year-start" onChange={(v) => setForm({ ...form, startDate: v })} />
        <DateField label="End date" value={form.endDate || ""} testId="year-end" onChange={(v) => setForm({ ...form, endDate: v })} />
        <Pick
          label="Moderator"
          options={activeMembers.map((m) => ({ value: m.id, label: m.name }))}
          value={form.moderatorId || ""}
          data-testid="year-moderator"
          onChange={(e) => setForm({ ...form, moderatorId: e.target.value })}
        />
        <Pick
          label="Co-Moderator"
          options={activeMembers.map((m) => ({ value: m.id, label: m.name }))}
          value={form.coModeratorId || ""}
          data-testid="year-comoderator"
          onChange={(e) => setForm({ ...form, coModeratorId: e.target.value })}
        />
        <p className="text-xs italic" style={{ color: "var(--ink-mute)" }}>Changing these transfers admin rights immediately.</p>
        <button className="btn-ink" data-testid="year-save" onClick={save}>Save year</button>
      </Card>

      <Card>
        <Kicker>Pass the table</Kicker>
        <p className="text-[16px] font-semibold mt-2" style={{ color: "var(--ink)" }}>Start the next year.</p>
        {year.endDate && new Date() > new Date(year.endDate) && (
          <div
            className="mt-3 px-3.5 py-2.5 rounded-lg text-[13px]"
            data-testid="rollover-due"
            style={{ background: "var(--amber-wash)", border: "1px solid #fde68a", color: "#92400e" }}
          >
            The forum year ended on {year.endDate}. Open the next year — this one is archived with its
            full record intact.
          </div>
        )}
        <p className="lede mt-2">
          Forum years run 1 July to 30 June. The current year is archived read-only with its full
          history; a new year opens with eight empty chapters and new chairs.
        </p>
        <button
          className="btn-ink mt-4"
          data-testid="rollover-open"
          onClick={() => {
            const next = Number(String(year.id).slice(0, 4)) + 1;
            setNw({
              newYearId: `${next}-${String(next + 1).slice(2)}`,
              theme: "",
              northStar: "",
              startDate: `${next}-07-01`,
              endDate: `${next + 1}-06-30`,
              moderatorId: year.coModeratorId || "",
              coModeratorId: "",
            });
            setWizard(true);
          }}
        >
          Start the next year
        </button>
      </Card>

      <Modal open={wizard} onClose={() => setWizard(false)} title="Pass the table" testId="rollover-modal">
        <Field label="New year id (e.g. 2027-28)" value={nw.newYearId} data-testid="rollover-year" onChange={(e) => setNw({ ...nw, newYearId: e.target.value })} />
        <Field label="Theme" value={nw.theme} onChange={(e) => setNw({ ...nw, theme: e.target.value })} />
        <Area label="North Star" value={nw.northStar} onChange={(e) => setNw({ ...nw, northStar: e.target.value })} />
        <DateField label="Start date" value={nw.startDate} testId="rollover-start" onChange={(v) => setNw({ ...nw, startDate: v })} />
        <DateField label="End date" value={nw.endDate} testId="rollover-end" onChange={(v) => setNw({ ...nw, endDate: v })} />
        <Pick
          label="Moderator"
          options={[{ value: "", label: "—" }, ...activeMembers.map((m) => ({ value: m.id, label: m.name }))]}
          value={nw.moderatorId}
          data-testid="rollover-moderator"
          onChange={(e) => setNw({ ...nw, moderatorId: e.target.value })}
        />
        <Pick
          label="Co-Moderator"
          options={[{ value: "", label: "—" }, ...activeMembers.map((m) => ({ value: m.id, label: m.name }))]}
          value={nw.coModeratorId}
          data-testid="rollover-comoderator"
          onChange={(e) => setNw({ ...nw, coModeratorId: e.target.value })}
        />
        <button className="btn-ink w-full" data-testid="rollover-confirm" onClick={pass}>Pass the table</button>
      </Modal>
    </div>
  );
};

const SettingsTab = () => {
  const { settings, setSettings } = useTable();  const [form, setForm] = useState(settings || { forumName: "", dimensions: [], appUrl: "" });
  const [audit, setAudit] = useState([]);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  useEffect(() => { api.get("/audit").then((r) => setAudit(r.data)).catch(() => {}); }, []);

  const save = async () => {
    try {
      const r = await api.put("/settings", { forumName: form.forumName, dimensions: form.dimensions, appUrl: form.appUrl });
      setSettings(r.data);
      toast.success("Settings saved.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const download = async () => {
    try {
      const r = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `the-table-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <Kicker>Forum</Kicker>
        <Field label="Forum name" value={form.forumName || ""} data-testid="settings-name" onChange={(e) => setForm({ ...form, forumName: e.target.value })} />
        <Field label="App URL" value={form.appUrl || ""} onChange={(e) => setForm({ ...form, appUrl: e.target.value })} />
        <Kicker>Baseline dimensions</Kicker>
        {(form.dimensions || []).map((d, i) => (
          <Field
            key={i}
            value={d}
            data-testid={`settings-dim-${i + 1}`}
            onChange={(e) => {
              const dims = [...form.dimensions];
              dims[i] = e.target.value;
              setForm({ ...form, dimensions: dims });
            }}
          />
        ))}
        <button className="btn-ink" data-testid="settings-save" onClick={save}>Save settings</button>
      </Card>

      <Card className="flex gap-3 flex-wrap items-center">
        {settings?.email?.paused && (
          <div
            className="w-full px-3.5 py-2.5 rounded-lg text-[13px]"
            data-testid="email-paused-banner"
            style={{ background: "var(--amber-wash)", border: "1px solid #fde68a", color: "#92400e" }}
          >
            <strong>Email is paused.</strong> No broadcasts, briefings or test emails will be sent.
            Set <code>EMAIL_PAUSED=false</code> in the backend environment to resume.
          </div>
        )}
        {!settings?.email?.paused && settings?.email?.remindersPaused && (
          <div
            className="w-full px-3.5 py-2.5 rounded-lg text-[13px]"
            data-testid="reminders-paused-banner"
            style={{ background: "var(--amber-wash)", border: "1px solid #fde68a", color: "#92400e" }}
          >
            <strong>Email is live.</strong> Highlight broadcasts are sending. The automatic
            chapter reminder is still switched off — set <code>REMINDERS_PAUSED=false</code> in the
            backend environment when you want briefings to go out on their own.
          </div>
        )}
        <button
          className="btn-quiet"
          data-testid="test-email"
          disabled={settings?.email?.paused}
          onClick={async () => {
            try { await api.post("/admin/test-email"); toast.success("Test email sent."); }
            catch (e) { toast.error(errMsg(e)); }
          }}
        >
          Send me a test email
        </button>
        <button className="btn-quiet" data-testid="export-json" onClick={download}>Download full JSON backup</button>
      </Card>

      <Card>
        <Kicker>Audit log</Kicker>
        <div className="mt-3 space-y-2 max-h-80 overflow-y-auto">
          {audit.length === 0 && <p className="text-sm italic" style={{ color: "var(--ink-mute)" }}>Nothing logged yet.</p>}
          {audit.map((a) => (
            <div key={a.id} className="text-xs flex gap-3" style={{ color: "var(--ink-mute)" }}>
              <span className="shrink-0" style={{ color: "var(--ink-mute)" }}>{relTime(a.timestamp)}</span>
              <span className="shrink-0">{a.userEmail}</span>
              <span>{a.summary}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default function Admin() {
  const [tab, setTab] = useState("Members");
  return (
    <div data-testid="admin-page">
      <PageHead kicker="The chair" title="Admin" sub="Rights follow the chairs of the active year." />
      <div className="flex gap-2 flex-wrap mb-6">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
            onClick={() => setTab(t)}
            className="chip"
            style={tab === t ? { background: "var(--ink-panel)", color: "#FFFFFF", borderColor: "var(--ink-panel)" } : {}}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="fade-in" key={tab}>
        {tab === "Members" && <MembersTab />}
        {tab === "Chapters" && <ChaptersTab />}
        {tab === "Year & Roles" && <YearTab />}
        {tab === "Settings" && <SettingsTab />}
      </div>
    </div>
  );
}
